import React, { useEffect, useMemo, useState } from 'react';
import JobMonitorCard from './JobMonitorCard';
import productionService from '../../../service/production.service';

const JobMonitorResources = ({
  resource,
  getProductionById,
  onDragStart,
  onDrop,
  isDragging,
  dragOverData,
  setIsAttachmentDialogOpen,
  setAttachments,
  autoCompleteData,
  onFeedbackClick,
  getSourceListAttachment,
}) => {
  const { draggingItemId, setDraggingItemId } = isDragging;
  const { dragOverIndex, setDragOverIndex } = dragOverData;

  const scrollId = `scrollableDiv-${resource.resourceOneId}`;
  const scrollSpeed = 100;
  const scrollThreshold = 150;

  const [extendedRows, setExtendedRows] = useState([]);

  useEffect(() => {
    loadInitialData();
  }, [resource]);

  const loadInitialData = () => {
    setExtendedRows(resource.rows ?? []);
  };

  const handleDragOver = (event, index) => {
    event.preventDefault();

    const container = event.currentTarget.closest('.custom-scrollbar');
    if (container) {
      const { top, bottom } = container.getBoundingClientRect();
      const mouseY = event.clientY;

      if (mouseY - top < scrollThreshold) {
        container.scrollBy({ top: -scrollSpeed, behavior: 'smooth' });
      } else if (bottom - mouseY < scrollThreshold) {
        container.scrollBy({ top: scrollSpeed, behavior: 'smooth' });
      }
    }

    if (extendedRows[index]?.productionoperationid === draggingItemId) {
      setDragOverIndex({ resourceId: null, index: null });
      return;
    }

    const draggingIndex = extendedRows.findIndex((op) => op.productionoperationid === draggingItemId);
    if (draggingIndex === index || draggingIndex + 1 === index) {
      setDragOverIndex({ resourceId: null, index: null });
    } else {
      setDragOverIndex({ resourceId: resource.resourceOneId, index });
    }
  };

  const handleDropInside = (event) => {
    event.preventDefault();

    const jobItem = JSON.parse(event.dataTransfer.getData('jobItem'));

    const fromIndex = extendedRows.findIndex((op) => op.productionoperationid === jobItem.productionoperationid);
    if (!extendedRows.some((op) => op.productionoperationid === jobItem.productionoperationid)) {
      onDrop(event, resource);
      setDragOverIndex(null);
      setDraggingItemId(null);
      return;
    }

    const toIndex = dragOverIndex.index;

    if (toIndex === null || fromIndex === toIndex) {
      setDragOverIndex(null);
      setDraggingItemId(null);
      return;
    }

    const reordered = reorderOperations(extendedRows, fromIndex, toIndex);

    const isSameOrder = extendedRows.every(
      (op, idx) => op.productionoperationid === reordered[idx].productionoperationid
    );
    if (!isSameOrder) {
      const payload = reordered.map((item, idx) => ({
        id: item.productionoperationid,
        visOrderY: idx + 1,
      }));

      productionService.updateVisOrderYBatch(payload);
    }
    setExtendedRows(reordered);
    setDragOverIndex(null);
    setDraggingItemId(null);
  };

  const totalHours = useMemo(() => {
    const totalMinutes = extendedRows.reduce((sum, r) => sum + (Number(r?.plannedQty) || 0), 0);
    return Math.round((totalMinutes / 60) * 100) / 100;
  }, [extendedRows]);

  const reorderOperations = (operations, startIndex, endIndex) => {
    const result = Array.from(operations);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);

    return result.map((operation, index) => ({
      ...operation,
      visOrderY: index + 1,
    }));
  };

  return (
    <div
      id={scrollId}
      className="flex flex-col max-h-[calc(100vh-140px)] min-h-[calc(100vh-140px)] bg-gray-100 overflow-y-auto custom-scrollbar"
      onDragOver={(event) => event.preventDefault()}
      onDrop={(event) => handleDropInside(event)}
    >
      <div className="bg-white pl-2 text-center font-bold relative py-3 text-labelColor">
        <div className="text-xl">{resource.resourceOneName}</div>
        <div>{totalHours} óra</div>
      </div>

      <div className="p-2">
        {extendedRows.map((row, index) => (
          <div key={`${row.productionoperationid}-${row.multiresourceId ? 'mr' : 'main'}`}>
            {dragOverIndex?.resourceId === resource.resourceOneId && dragOverIndex.index === index && (
              <div
                className="mb-3 rounded-md p-4 shadow-md bg-gray-300 border-2 border-gray-500"
                style={{ minHeight: '200px' }}
              ></div>
            )}
            <div onDragOver={(event) => handleDragOver(event, index)}>
              <JobMonitorCard
                data={row}
                getProductionById={getProductionById}
                onDragStart={onDragStart}
                type="RESOURCECOLUMN"
                setIsAttachmentDialogOpen={setIsAttachmentDialogOpen}
                setAttachments={setAttachments}
                autoCompleteData={autoCompleteData}
                onFeedbackClick={onFeedbackClick}
                getSourceListAttachment={getSourceListAttachment}
                isSelected={false}
                onSelectToggle={null}
              />
            </div>
          </div>
        ))}

        {dragOverIndex?.resourceId === resource.resourceOneId && dragOverIndex.index === extendedRows.length && (
          <div
            className="mb-3 rounded-md p-4 shadow-md bg-gray-300 border-2 border-gray-500"
            style={{ minHeight: '200px' }}
          ></div>
        )}
      </div>
    </div>
  );
};

export default JobMonitorResources;
